import React from 'react';
import { useAuth } from './AuthContext';
import TenantDropdown from './TenantDropdown';
import Icon from './Icon';
import './Header.css';

const Header = ({ onSettingsClick, onMenuClick }) => {
  const { user, logout, selectedTenant, setSelectedTenant, availableTenants, hasAllTenantAccess } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const handleSettingsClick = (e) => {
    e.preventDefault();
    onSettingsClick();
  };

  const handleLogout = () => {
    logout();
  };

  const handleUserListClick = (e) => {
    e.preventDefault();
    if (onMenuClick) {
      onMenuClick('tenantUsers');
    }
  };

  const handleContactsClick = (e) => {
    e.preventDefault();
    if (onMenuClick) {
      onMenuClick('contacts');
    }
  };

  const handleOverviewClick = (e) => {
    e.preventDefault();
    if (onMenuClick) {
      onMenuClick('overview');
    }
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    if (onMenuClick) {
      onMenuClick('dashboard');
    }
  };

  const logoFile = selectedTenant?.logofilename;
  const cacheKey = selectedTenant?.id || selectedTenant?.name || '';
  const [logoSrc, setLogoSrc] = React.useState('/SolidITlogo.png');

  React.useEffect(() => {
    // Build encoded path from public folder with leading slash, support subfolders and spaces
    const buildEncodedPath = (file) => {
      if (typeof file !== 'string') return null;
      const trimmed = file.trim();
      if (!trimmed) return null;
      const hasLeadingSlash = trimmed.startsWith('/');
      const parts = trimmed.split('/').filter(Boolean).map(encodeURIComponent);
      const joined = parts.join('/');
      const base = `${hasLeadingSlash ? '/' : '/'}${joined}`;
      const sep = base.includes('?') ? '&' : '?';
      return `${base}${sep}t=${encodeURIComponent(cacheKey)}`;
    };

    const candidate = buildEncodedPath(logoFile);
    if (!candidate) {
      setLogoSrc('/SolidITlogo.png');
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setLogoSrc(candidate);
    };
    img.onerror = () => {
      if (!cancelled) setLogoSrc('/SolidITlogo.png');
    };
    img.src = candidate;
    return () => { cancelled = true; };
  }, [logoFile, cacheKey]);
  const widthNum = Number(selectedTenant?.wide);
  const heightNum = Number(selectedTenant?.height);
  const logoWidth = Number.isFinite(widthNum) && widthNum > 0 ? `${widthNum}px` : undefined;
  const logoHeight = Number.isFinite(heightNum) && heightNum > 0 ? `${heightNum}px` : undefined;

  return (
    <header className="header">
      <nav className="nav">
        <div className="nav-brand">
          <img
            src={logoSrc}
            alt={selectedTenant?.name ? `${selectedTenant.name} logo` : "SOLID IT"}
            className="logo-image"
            style={{ width: logoWidth, height: logoHeight }}
          />
        </div>
        <div className="nav-center">
          <ul className="nav-links">
            <li>
              <a href="#home" className="nav-link" onClick={handleHomeClick}>Home</a>
            </li>
            <li>
              <a href="#about" className="nav-link">About</a>
            </li>
            <li>
              <a href="#contacts" className="nav-link" onClick={handleContactsClick}>
                <Icon name="contacts" size="small" className="nav-icon" />
                Contact
              </a>
            </li>
          </ul>
        </div>
        <div className="nav-right">
          {Array.isArray(availableTenants) && availableTenants.length > 1 && (
            <TenantDropdown 
              selectedTenant={selectedTenant}
              onTenantChange={setSelectedTenant}
            />
          )}
          <div className="user-section">
            <button className="user-icon" title={`Welcome, ${user?.username}`} onClick={handleUserListClick}>
              <Icon name="user" size="small" className="user-icon" />
            </button>
            {isAdmin && hasAllTenantAccess && (
              <button onClick={handleOverviewClick} className="overview-link" title="Tenant overview">
                <Icon name="overview" size="small" className="overview-icon" />
              </button>
            )}
            {isAdmin && (
              <button onClick={handleSettingsClick} className="settings-link" title="Settings">
                <Icon name="settings" size="small" className="settings-icon" />
              </button>
            )}
            <button onClick={handleLogout} className="logout-link" title="Logout">
              <Icon name="logout" size="small" className="logout-icon" />
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
